/**
 * CSV Import/Export utilities for Budget app
 */

import { Budget, BudgetEntry, BudgetSubject, BudgetSection, MeasureType } from '../types/budget.types';

// CSV Export Types
interface ExportOptions {
  includeHeaders?: boolean;
  delimiter?: string;
  dateFormat?: string;
}

interface CsvRow {
  [key: string]: string | number | boolean | null | undefined;
}

// Escape CSV field values
const escapeCSVField = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Parse CSV line handling quoted fields
const parseCSVLine = (line: string, delimiter: string = ','): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }

  result.push(current.trim());
  return result;
};

// Export budget entries to CSV
export const exportEntriesToCSV = (
  entries: BudgetEntry[],
  subjects: BudgetSubject[],
  sections: BudgetSection[],
  options: ExportOptions = {}
): string => {
  const { includeHeaders = true, delimiter = ',' } = options;

  const subjectMap = new Map(subjects.map(s => [s.id, s]));
  const sectionMap = new Map(sections.map(s => [s.id, s]));

  const headers = ['Period', 'Subject', 'Subject Type', 'Section', 'Measure', 'Amount', 'Notes'];

  const rows = entries.map(entry => {
    const subject = subjectMap.get(entry.subjectId);
    const section = entry.sectionId ? sectionMap.get(entry.sectionId) : null;

    return [
      escapeCSVField(entry.period),
      escapeCSVField(subject?.name || ''),
      escapeCSVField(subject?.type || ''),
      escapeCSVField(section?.name || ''),
      escapeCSVField(entry.measure),
      escapeCSVField(entry.amount),
      escapeCSVField(entry.notes || ''),
    ].join(delimiter);
  });

  if (includeHeaders) {
    return [headers.join(delimiter), ...rows].join('\n');
  }
  return rows.join('\n');
};

// Export budget configuration to CSV
export const exportBudgetConfigToCSV = (
  budget: Budget,
  options: ExportOptions = {}
): string => {
  const { delimiter = ',' } = options;

  const lines: string[] = [];

  // Budget metadata
  lines.push('# Budget Configuration');
  lines.push(`Name${delimiter}${escapeCSVField(budget.name)}`);
  lines.push(`Description${delimiter}${escapeCSVField(budget.description || '')}`);
  lines.push(`Interval${delimiter}${escapeCSVField(budget.intervalType)}`);
  lines.push(`Start Date${delimiter}${escapeCSVField(budget.startDate)}`);
  lines.push(`End Date${delimiter}${escapeCSVField(budget.endDate)}`);
  lines.push('');

  // Subjects
  lines.push('# Subjects');
  lines.push(['ID', 'Name', 'Type', 'Order', 'Parent ID', 'Goal %'].join(delimiter));
  budget.subjects.forEach(subject => {
    lines.push([
      escapeCSVField(subject.id),
      escapeCSVField(subject.name),
      escapeCSVField(subject.type),
      escapeCSVField(subject.order),
      escapeCSVField(subject.parentId || ''),
      escapeCSVField(subject.goalPercentage || ''),
    ].join(delimiter));
  });
  lines.push('');

  // Sections
  lines.push('# Sections');
  lines.push(['ID', 'Name', 'Subject ID', 'Order'].join(delimiter));
  budget.sections.forEach(section => {
    lines.push([
      escapeCSVField(section.id),
      escapeCSVField(section.name),
      escapeCSVField(section.subjectId),
      escapeCSVField(section.order),
    ].join(delimiter));
  });

  return lines.join('\n');
};

// Parse CSV string to entries
export interface ParsedCSVEntry {
  period: string;
  subjectName: string;
  subjectType: string;
  sectionName: string;
  measure: MeasureType;
  amount: number;
  notes: string;
}

export const parseEntriesFromCSV = (
  csvContent: string,
  delimiter: string = ','
): ParsedCSVEntry[] => {
  const lines = csvContent.split('\n').filter(line => line.trim() && !line.startsWith('#'));
  if (lines.length < 2) return [];

  // Skip header
  const dataLines = lines.slice(1);

  return dataLines.map(line => {
    const fields = parseCSVLine(line, delimiter);
    return {
      period: fields[0] || '',
      subjectName: fields[1] || '',
      subjectType: fields[2] || '',
      sectionName: fields[3] || '',
      measure: (fields[4] || 'actual') as MeasureType,
      amount: parseFloat(fields[5]) || 0,
      notes: fields[6] || '',
    };
  }).filter(entry => entry.subjectName && entry.period);
};

// Convert parsed entries to BudgetEntry objects
export const convertParsedEntriesToBudgetEntries = (
  parsedEntries: ParsedCSVEntry[],
  budgetId: string,
  subjects: BudgetSubject[],
  sections: BudgetSection[]
): Partial<BudgetEntry>[] => {
  const subjectNameMap = new Map(subjects.map(s => [s.name.toLowerCase(), s]));
  const sectionNameMap = new Map(sections.map(s => [s.name.toLowerCase(), s]));

  const results: Partial<BudgetEntry>[] = [];

  parsedEntries.forEach(parsed => {
    const subject = subjectNameMap.get(parsed.subjectName.toLowerCase());
    const section = parsed.sectionName ? sectionNameMap.get(parsed.sectionName.toLowerCase()) : null;

    if (!subject) {
      console.warn(`Subject not found: ${parsed.subjectName}`);
      return;
    }

    results.push({
      budgetId,
      subjectId: subject.id,
      sectionId: section?.id,
      period: parsed.period,
      amount: parsed.amount,
      measure: parsed.measure,
      notes: parsed.notes,
    });
  });

  return results;
};

// Download helper
export const downloadCSV = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

// Read file as text
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

// Validate CSV structure
export interface CSVValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  rowCount: number;
}

export const validateCSVStructure = (csvContent: string): CSVValidationResult => {
  const result: CSVValidationResult = {
    isValid: true,
    errors: [],
    warnings: [],
    rowCount: 0,
  };

  const lines = csvContent.split('\n').filter(line => line.trim() && !line.startsWith('#'));

  if (lines.length === 0) {
    result.isValid = false;
    result.errors.push('CSV file is empty');
    return result;
  }

  const headers = parseCSVLine(lines[0]);
  const requiredHeaders = ['Period', 'Subject', 'Measure', 'Amount'];

  requiredHeaders.forEach(header => {
    if (!headers.some(h => h.toLowerCase() === header.toLowerCase())) {
      result.errors.push(`Missing required header: ${header}`);
      result.isValid = false;
    }
  });

  result.rowCount = lines.length - 1;

  // Validate data rows
  lines.slice(1).forEach((line, index) => {
    const fields = parseCSVLine(line);

    if (fields.length !== headers.length) {
      result.warnings.push(`Row ${index + 2}: Column count mismatch`);
    }

    // Validate amount is numeric
    const amountIndex = headers.findIndex(h => h.toLowerCase() === 'amount');
    if (amountIndex !== -1 && fields[amountIndex]) {
      const amount = parseFloat(fields[amountIndex]);
      if (isNaN(amount)) {
        result.errors.push(`Row ${index + 2}: Invalid amount value "${fields[amountIndex]}"`);
        result.isValid = false;
      }
    }
  });

  return result;
};
